<script lang="ts">
  import { onMount } from 'svelte';
  import { Chart, registerables } from 'chart.js';
  import { calculatePercentage } from '../lib/utils/calculations';
  import type { Summary } from '../types/transaction';

  let { summary, transactions }: { summary: Summary | null, transactions?: any[] } = $props();
  let canvas: HTMLCanvasElement;
  let chart: Chart | null = null;

  onMount(() => {
    Chart.register(...registerables);
    renderChart();

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  });

  // Re-render chart when summary or transactions change
  $effect(() => {
    if (canvas) {
      renderChart();
    }
  });

  function renderChart() {
    // Destroy existing chart before creating new one
    if (chart) {
      chart.destroy();
      chart = null;
    }

    // Guard: Check if transactions are available
    if (!transactions || transactions.length === 0) {
      return;
    }

    // Calculate income breakdown from transactions (amount > 0 and not excluded)
    const incomeByCategory: Record<string, number> = {};

    // Define what qualifies as income categories (case-insensitive matching)
    const incomeKeywords = ['salary', 'wage', 'income', 'interest', 'dividend', 'bonus', 'windfall', 'tax return', 'refund', 'reimbursement'];

    for (const txn of transactions) {
      if (txn.amount > 0 && !txn.excluded) {
        const category = txn.category || 'Uncategorized';
        const categoryLower = category.toLowerCase();

        // Skip transfers and credit card payments
        if (categoryLower.includes('transfer') ||
            categoryLower.includes('credit card payment')) {
          continue;
        }

        // Only include if it matches income keywords or is uncategorized
        const isIncomeCategory = incomeKeywords.some(keyword => categoryLower.includes(keyword)) ||
                                 category === 'Uncategorized';

        if (!isIncomeCategory) {
          continue;
        }

        incomeByCategory[category] = (incomeByCategory[category] || 0) + txn.amount;
      }
    }

    // Get income categories sorted by amount
    const allIncomeCategories = Object.entries(incomeByCategory)
      .sort(([, a], [, b]) => b - a);

    // If no income categories found, don't render chart
    if (allIncomeCategories.length === 0) {
      return;
    }

    // Take top 15 categories, group the rest as "All Others"
    const topCategories = allIncomeCategories.slice(0, 15);
    const remainingCategories = allIncomeCategories.slice(15);

    const mainCategories: [string, number][] = [...topCategories];

    // Add "All Others" if there are remaining items
    if (remainingCategories.length > 0) {
      const othersAmount = remainingCategories.reduce((sum, [, amount]) => sum + amount, 0);
      mainCategories.push(['All Others', othersAmount]);
    }

    const labels = mainCategories.map(([name]) => name);
    const data = mainCategories.map(([, amount]) => amount);

    // Get colors from CSS variables - using green/blue shades for income
    const rootStyles = getComputedStyle(document.documentElement);
    const colors = [
      rootStyles.getPropertyValue('--chart-color-2').trim(), // Green
      rootStyles.getPropertyValue('--chart-color-7').trim(), // Cyan
      rootStyles.getPropertyValue('--chart-color-5').trim(), // Purple
      rootStyles.getPropertyValue('--chart-color-1').trim(), // Blue
      rootStyles.getPropertyValue('--chart-color-10').trim(), // Indigo
      rootStyles.getPropertyValue('--chart-color-8').trim(), // Lime
      rootStyles.getPropertyValue('--chart-color-3').trim(), // Orange
      rootStyles.getPropertyValue('--chart-color-6').trim(), // Pink
      rootStyles.getPropertyValue('--chart-color-4').trim(), // Red
      rootStyles.getPropertyValue('--chart-color-9').trim(), // Deep orange
    ];

    const borderColor = rootStyles.getPropertyValue('--color-text-primary').trim();
    const legendColor = rootStyles.getPropertyValue('--color-text-muted').trim();

    chart = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: colors,
          borderColor: borderColor,
          borderWidth: 2,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            align: 'start',
            labels: {
              color: legendColor,
              font: {
                size: 11
              },
              boxWidth: 12,
              padding: 8,
              usePointStyle: true,
              pointStyle: 'circle'
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const value = context.parsed;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = calculatePercentage(value, total).toFixed(1);
                const formatted = new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(value);
                return `${formatted} (${percentage}%)`;
              }
            }
          }
        }
      }
    });
  }
</script>

<div class="chart-container">
  <canvas bind:this={canvas}></canvas>
</div>

<style lang="scss">
  .chart-container {
    position: relative;
    width: 100%;
    height: 400px;
  }
</style>
